import { generateClient } from "aws-amplify/api";
import {
  buildProjectConflictCopy,
  detectProjectConflicts,
  normalizeConflictSnapshot,
  type ProjectConflictActor,
  type ProjectConflictField,
  type ProjectConflictOperation,
  type ProjectConflictTarget,
} from "./projectConflictCopy";
import { confirmProjectConflict as openProjectConflictConfirm } from "../components/BlingsModal/ProjectConflictConfirm";
import {
  listProjectChangeActivities,
  listProjectVersionChangeActivities,
} from "../graphql/queries";
import { getAccessTokenForLambdaAuth } from "./authHelpers";

type BaselineKey = `${string}:${ProjectConflictTarget}`;

type GuardProjectMutationParams = {
  projectId: string;
  target: ProjectConflictTarget;
  operation: ProjectConflictOperation;
  fields: ProjectConflictField[];
  next?: unknown;
  loadLatest: () => Promise<unknown>;
};

const client = generateClient();
const baselines = new Map<BaselineKey, unknown>();

function getBaselineKey(
  projectId: string,
  target: ProjectConflictTarget
): BaselineKey {
  return `${projectId}:${target}`;
}

function getFieldPath(field: ProjectConflictField): string {
  return typeof field === "string" ? field : field.path;
}

function getTopLevelField(path: string): string {
  return path.split(".").find(Boolean) || path;
}

function mergeBaselineFields(
  baseline: unknown,
  fields: ProjectConflictField[],
  values: unknown
) {
  const nextBaseline =
    baseline && typeof baseline === "object"
      ? { ...(baseline as Record<string, unknown>) }
      : {};
  const source = (values || {}) as Record<string, unknown>;

  fields.forEach((field) => {
    const topLevelField = getTopLevelField(getFieldPath(field));
    if (Object.prototype.hasOwnProperty.call(source, topLevelField)) {
      nextBaseline[topLevelField] = normalizeConflictSnapshot(
        source[topLevelField]
      );
    }
  });

  return nextBaseline;
}

export function setProjectConflictBaseline(params: {
  projectId: string;
  target: ProjectConflictTarget;
  snapshot: unknown;
}) {
  baselines.set(
    getBaselineKey(params.projectId, params.target),
    normalizeConflictSnapshot(params.snapshot)
  );
}

export function updateProjectConflictBaselineFields(params: {
  projectId: string;
  target: ProjectConflictTarget;
  fields: ProjectConflictField[];
  values: unknown;
}) {
  const key = getBaselineKey(params.projectId, params.target);
  const baseline = baselines.get(key);
  baselines.set(
    key,
    mergeBaselineFields(baseline, params.fields, params.values)
  );
}

function getLatestTimestamp(latest: unknown): string | null {
  const candidate = latest as { updatedAt?: string | null; publishedAt?: string | null };
  return candidate?.publishedAt || candidate?.updatedAt || null;
}

async function loadLatestActor(params: {
  projectId: string;
  target: ProjectConflictTarget;
  latest: unknown;
}): Promise<ProjectConflictActor> {
  try {
    const authToken = await getAccessTokenForLambdaAuth();
    const query =
      params.target === "published"
        ? listProjectChangeActivities
        : listProjectVersionChangeActivities;
    const response = (await client.graphql({
      query,
      variables: {
        filter: {
          projectId: {
            eq: params.projectId,
          },
        },
        limit: 20,
      },
      authMode: "lambda",
      authToken,
    })) as {
      data?: {
        listProjectChangeActivities?: {
          items?: Array<{
            owner?: string | null;
            email?: string | null;
            createdAt?: string | null;
          } | null> | null;
        } | null;
        listProjectVersionChangeActivities?: {
          items?: Array<{
            owner?: string | null;
            createdAt?: string | null;
          } | null> | null;
        } | null;
      };
    };

    const items =
      params.target === "published"
        ? response.data?.listProjectChangeActivities?.items
        : response.data?.listProjectVersionChangeActivities?.items;
    const latestActivity = (items || [])
      .filter(Boolean)
      .sort((a, b) =>
        ((b?.createdAt || "") > (a?.createdAt || "") ? 1 : -1)
      )[0] as
      | {
          owner?: string | null;
          email?: string | null;
          createdAt?: string | null;
        }
      | undefined;

    return {
      actor:
        params.target === "published"
          ? latestActivity?.email || latestActivity?.owner || null
          : latestActivity?.owner || null,
      occurredAt: latestActivity?.createdAt || getLatestTimestamp(params.latest),
    };
  } catch (error) {
    console.warn("Failed to load project conflict actor", error);
    return {
      actor: null,
      occurredAt: getLatestTimestamp(params.latest),
    };
  }
}

async function confirmProjectConflict(params: {
  actor: ProjectConflictActor;
  conflicts: ReturnType<typeof detectProjectConflicts>;
  operation: ProjectConflictOperation;
  target: ProjectConflictTarget;
}) {
  const copy = buildProjectConflictCopy({
    conflicts: params.conflicts,
    target: params.target,
    operation: params.operation,
    actor: params.actor,
  });

  return openProjectConflictConfirm(copy);
}

export async function guardProjectMutation({
  projectId,
  target,
  operation,
  fields,
  next,
  loadLatest,
}: GuardProjectMutationParams): Promise<boolean> {
  try {
    const latest = await loadLatest();
    const key = getBaselineKey(projectId, target);
    const baseline = baselines.get(key);
    if (!baseline) {
      baselines.set(key, normalizeConflictSnapshot(latest));
      return true;
    }

    const conflicts = detectProjectConflicts({
      baseline,
      latest,
      next,
      fields,
    });
    if (!conflicts.length) {
      return true;
    }

    const actor = await loadLatestActor({ projectId, target, latest });
    return await confirmProjectConflict({
      actor,
      conflicts,
      operation,
      target,
    });
  } catch (error) {
    console.warn("Project conflict preflight failed; allowing mutation", error);
    return true;
  }
}
